import { useState, useEffect, useCallback } from 'react';
import {
  Search, Loader2, CheckCircle2, XCircle, AlertTriangle, HelpCircle, Building2,
  Database, Receipt, Undo2, Eye, EyeOff,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VERDICT = {
  yes: { icon: CheckCircle2, color: 'text-green-700', bg: 'bg-green-50 border-green-200', label: 'Confirmado en banco' },
  likely: { icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', label: 'Probable (evidencia interna)' },
  partial: { icon: AlertTriangle, color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', label: 'Coincidencia parcial' },
  no: { icon: XCircle, color: 'text-red-700', bg: 'bg-red-50 border-red-200', label: 'No encontrado en banco' },
  unknown: { icon: HelpCircle, color: 'text-slate-600', bg: 'bg-slate-50 border-slate-200', label: 'Sin evidencia' },
};

const fmt = (n) => (Number(n) || 0).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Section({ icon: Icon, title, count, children }) {
  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 border-b flex items-center gap-2">
        <Icon className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">{count}</span>
      </div>
      <div className="p-3 space-y-2">{children}</div>
    </div>
  );
}

export default function PaymentVerifierTab({ setToast }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ account: '', reference: '', amount: '', date: '', days_window: 1 });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [affectLedger, setAffectLedger] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      const tk = localStorage.getItem('token');
      const r = await axios.get(`${BACKEND_URL}/api/super-admin/payment-verifier/accounts`, { headers: { Authorization: `Bearer ${tk}` } });
      setAccounts(r.data.accounts || []);
    } catch (e) {
      console.warn('load accounts failed', e);
    }
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.account) { setToast?.({ type: 'error', message: 'Selecciona o ingresa una cuenta' }); return; }
    if (!form.reference && !form.amount) { setToast?.({ type: 'error', message: 'Indica al menos referencia o monto' }); return; }
    setBusy(true); setError(null); setResult(null);
    try {
      const tk = localStorage.getItem('token');
      const params = { account: form.account, days_window: form.days_window };
      if (form.reference) params.reference = form.reference.trim();
      if (form.amount) params.amount = parseFloat(form.amount);
      if (form.date) params.date = form.date;
      const r = await axios.get(`${BACKEND_URL}/api/super-admin/payment-verifier`, { params, headers: { Authorization: `Bearer ${tk}` } });
      setResult(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? JSON.stringify(detail) : (detail || err.message);
      setError(msg);
      setToast?.({ type: 'error', message: msg });
    } finally {
      setBusy(false);
    }
  };

  const verdict = result?.verdict?.entered_bank ? (VERDICT[result.verdict.entered_bank] || VERDICT.unknown) : null;
  const VIcon = verdict?.icon;

  const canReconcile = result
    && result.verdict.reconciled === 'no'
    && ['yes', 'likely', 'partial'].includes(result.verdict.entered_bank)
    && result.query.amount != null
    && !!result.query.reference;

  const reconcileManual = async () => {
    if (!canReconcile) return;
    const q = result.query;
    const ledgerNote = affectLedger
      ? '\n\n⚠️ AFECTARÁ EL SALDO DEL LEDGER (+monto). Usar solo si el pago AÚN no fue liquidado/barrido.'
      : '\n\nNO afectará el saldo del ledger (solo registro/auditoría).';
    if (!window.confirm(`Registrar CONCILIACIÓN MANUAL\n\nCuenta: …${q.account.slice(-4)}\nMonto: VES ${fmt(q.amount)}\nReferencia: ${q.reference}\nFecha: ${q.date}${ledgerNote}`)) return;
    setReconciling(true);
    try {
      const tk = localStorage.getItem('token');
      const r = await axios.post(`${BACKEND_URL}/api/super-admin/payment-verifier/reconcile-manual`, {
        account: q.account,
        amount: q.amount,
        payment_reference: q.reference,
        date: q.date,
        affect_ledger: affectLedger,
      }, { headers: { Authorization: `Bearer ${tk}` } });
      setToast?.({ type: 'success', message: `${r.data.message} · ${r.data.merchant || ''}` });
      submit(new Event('resubmit')); // re-verificar para reflejar el nuevo estado
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? JSON.stringify(detail) : (detail || err.message);
      setToast?.({ type: 'error', message: msg });
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div data-testid="payment-verifier-tab">
      <div className="mb-4">
        <h2 className="text-2xl font-bold" style={{ color: '#051C2C' }}>Verificador de Pagos</h2>
        <p className="text-sm text-slate-600 mt-1">
          Cruza un pago (cuenta + monto + fecha + referencia) <strong>en vivo contra los movimientos del banco</strong> y contra nuestra base de datos (órdenes, ledger, comisiones y refunds).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Formulario */}
        <div className="bg-white border rounded-lg p-5">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase">Cuenta</label>
              <select value={form.account} onChange={update('account')} data-testid="verifier-account-select"
                className="w-full px-3 py-2 border rounded text-sm mt-1">
                <option value="">— Selecciona una cuenta —</option>
                {accounts.map((a) => (
                  <option key={a.account} value={a.account}>{a.label} · …{a.account.slice(-4)}</option>
                ))}
              </select>
              <input value={form.account} onChange={update('account')} data-testid="verifier-account-input"
                placeholder="…o pega la cuenta de 20 dígitos" maxLength={20}
                className="w-full px-3 py-2 border rounded text-sm font-mono mt-2" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase">Referencia</label>
              <input value={form.reference} onChange={update('reference')} data-testid="verifier-reference"
                placeholder="Ej: 000886673736"
                className="w-full px-3 py-2 border rounded text-sm font-mono mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase">Monto VES</label>
                <input type="number" step="0.01" value={form.amount} onChange={update('amount')} data-testid="verifier-amount"
                  placeholder="10651.53"
                  className="w-full px-3 py-2 border rounded text-sm font-mono mt-1" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-500 uppercase">Fecha</label>
                <input type="date" value={form.date} onChange={update('date')} data-testid="verifier-date"
                  className="w-full px-3 py-2 border rounded text-sm mt-1" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-500 uppercase">Rango ± días</label>
              <select value={form.days_window} onChange={update('days_window')} data-testid="verifier-days"
                className="w-full px-3 py-2 border rounded text-sm mt-1">
                <option value={0}>Mismo día</option>
                <option value={1}>± 1 día</option>
                <option value={2}>± 2 días</option>
                <option value={3}>± 3 días</option>
                <option value={7}>± 7 días</option>
              </select>
            </div>
            <button type="submit" disabled={busy} data-testid="verifier-submit"
              className="w-full px-4 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {busy ? 'Verificando…' : 'Verificar pago'}
            </button>
            {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="verifier-error">{error}</div>}
          </form>
        </div>

        {/* Resultados */}
        <div className="lg:col-span-2 space-y-4">
          {!result && !busy && (
            <div className="border border-dashed rounded-lg p-10 text-center text-slate-400 text-sm" data-testid="verifier-empty">
              Ingresa los datos del pago y presiona “Verificar pago”.
            </div>
          )}

          {result && (
            <>
              {/* Veredicto */}
              <div className={`border rounded-lg p-4 ${verdict?.bg}`} data-testid="verifier-verdict">
                <div className="flex items-center gap-2 mb-2">
                  {VIcon && <VIcon className={`w-5 h-5 ${verdict.color}`} />}
                  <span className={`font-bold ${verdict?.color}`}>{verdict?.label}</span>
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${result.verdict.reconciled === 'yes' ? 'bg-green-200 text-green-900' : 'bg-slate-200 text-slate-700'}`}>
                    {result.verdict.reconciled === 'yes' ? `Conciliado · ${result.verdict.reconciled_order_code}` : 'Sin conciliar'}
                  </span>
                </div>
                <ul className="space-y-1">
                  {result.summary.map((s, i) => (
                    <li key={i} className="text-sm text-slate-700">{s}</li>
                  ))}
                </ul>
                {!result.bank.available && (
                  <div className="mt-2 text-[11px] text-amber-800 bg-amber-100/60 rounded p-2">
                    Banco no disponible en este entorno: <span className="font-mono">{result.bank.error}</span>. Corre esta misma consulta desde la app en <strong>producción</strong> para el cruce en vivo.
                  </div>
                )}
                {canReconcile && (
                  <div className="mt-3 pt-3 border-t border-black/5 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer" data-testid="verifier-affect-ledger-label">
                      <input type="checkbox" checked={affectLedger} onChange={(e) => setAffectLedger(e.target.checked)}
                        data-testid="verifier-affect-ledger" className="rounded" />
                      <span className="text-xs text-slate-700">
                        Afectar saldo del ledger <span className="text-slate-400">(solo si el pago AÚN no fue liquidado/barrido)</span>
                      </span>
                    </label>
                    <div className="flex items-center gap-3 flex-wrap">
                      <button onClick={reconcileManual} disabled={reconciling} data-testid="verifier-reconcile-btn"
                        className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2">
                        {reconciling ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        {reconciling ? 'Registrando…' : 'Conciliar manualmente'}
                      </button>
                      <span className="text-[11px] text-slate-500">
                        {affectLedger
                          ? 'Creará la orden y SUMARÁ el crédito al ledger del comercio.'
                          : 'Creará la orden como registro/auditoría, sin tocar el saldo del ledger.'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Movimientos banco */}
              <Section icon={Building2} title="Movimientos del banco (coincidencias)" count={result.bank.total_found}>
                {result.bank.matches.length === 0 ? (
                  <div className="text-xs text-slate-400">{result.bank.available ? 'Sin coincidencias en el rango.' : 'Banco no consultado.'}</div>
                ) : result.bank.matches.map((m, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm border-b last:border-0 pb-2 last:pb-0" data-testid={`verifier-bank-mov-${i}`}>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${m.naturaleza === 'CR' ? 'bg-green-200 text-green-900' : 'bg-red-200 text-red-900'}`}>{m.naturaleza}</span>
                    <span className="font-mono font-semibold">VES {fmt(m.monto)}</span>
                    <span className="font-mono text-xs text-slate-500">ref {m.referencia}</span>
                    <span className="text-xs text-slate-400">{m.fecha}</span>
                    <span className="ml-auto text-[10px] text-blue-700">{m.match_reasons.join(', ')}</span>
                  </div>
                ))}
              </Section>

              {/* Órdenes */}
              <Section icon={Database} title="Órdenes en Caura" count={result.db.orders.length}>
                {result.db.orders.length === 0 ? (
                  <div className="text-xs text-slate-400">Ninguna orden con esa referencia/monto.</div>
                ) : result.db.orders.map((o, i) => (
                  <div key={i} className="flex items-center gap-3 text-sm border-b last:border-0 pb-2 last:pb-0" data-testid={`verifier-order-${i}`}>
                    <span className="font-mono font-semibold">{o.order_code}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${o.status === 'paid' ? 'bg-green-200 text-green-900' : o.status === 'refunded' ? 'bg-purple-200 text-purple-900' : 'bg-slate-200 text-slate-700'}`}>{o.status}</span>
                    <span className="font-mono text-xs">VES {fmt(o.amount)}</span>
                    <span className="font-mono text-xs text-slate-500">ref {o.payment_reference || '—'}</span>
                    {o.ref_match && <span className="text-[10px] text-green-700">✓ ref</span>}
                  </div>
                ))}
              </Section>

              {/* Ledger + comisiones + refunds */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Section icon={Database} title="Ledger" count={result.db.ledger_entries.length}>
                  {result.db.ledger_entries.length === 0 ? <div className="text-xs text-slate-400">—</div> :
                    result.db.ledger_entries.map((e, i) => (
                      <div key={i} className="text-xs" data-testid={`verifier-ledger-${i}`}>
                        <span className="font-semibold">{e.entry_type}</span> · VES {fmt(e.amount)}
                        <div className="text-slate-400 font-mono">ref {e.bank_reference || '—'}</div>
                      </div>
                    ))}
                </Section>
                <Section icon={Receipt} title="Comisiones" count={result.db.commission_captures.length}>
                  {result.db.commission_captures.length === 0 ? <div className="text-xs text-slate-400">—</div> :
                    result.db.commission_captures.map((c, i) => (
                      <div key={i} className="text-xs" data-testid={`verifier-capture-${i}`}>
                        VES {fmt(c.mov_amount)} · <span className="text-slate-400 font-mono">ref {c.mov_reference}</span>
                        <div className="text-slate-400">{c.mov_date} {c.mov_time}</div>
                      </div>
                    ))}
                </Section>
                <Section icon={Undo2} title="Refunds" count={result.db.refunds.length}>
                  {result.db.refunds.length === 0 ? <div className="text-xs text-slate-400">—</div> :
                    result.db.refunds.map((r, i) => (
                      <div key={i} className="text-xs" data-testid={`verifier-refund-${i}`}>
                        VES {fmt(r.amount)} · {r.status}
                      </div>
                    ))}
                </Section>
              </div>

              <button onClick={() => setShowRaw((s) => !s)} className="text-xs px-2 py-1 border rounded inline-flex items-center gap-1 text-slate-500" data-testid="verifier-toggle-raw">
                {showRaw ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {showRaw ? 'Ocultar JSON' : 'Ver JSON crudo'}
              </button>
              {showRaw && (
                <pre className="bg-slate-900 text-green-300 p-3 rounded overflow-x-auto text-xs" data-testid="verifier-raw">{JSON.stringify(result, null, 2)}</pre>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
